import * as THREE from "three";

/**
 * ADR 204: fit the camera to what is actually on the floor.
 *
 * The /live camera used to aim at the DEMO seed's 60×40 m center (the same
 * static `zones.json` the WarehouseModel auto-fit trap came from — see
 * ADR 158's post-mortem): any real warehouse loaded off-center, and the
 * "Corredor"/"Docas" presets pointed at demo coordinates. These helpers derive
 * the pose from the scene's real content bounds instead.
 *
 * Bounds are computed by hand because `Box3.expandByObject` ignores instance
 * matrices ("instancing isn't supported so far" — three r185), and at JSL
 * scale ~85k of our meshes are instanced (ADR 197/199 decks, racks, loads).
 * Instance TRANSLATIONS are enough: a bin is ≤ ~1.5 m and the floor is ~100 m,
 * so the sub-metre truncation disappears inside the fit margin.
 */

const _v = new THREE.Vector3();
const _b = new THREE.Box3();

export function contentBounds(root: THREE.Object3D): THREE.Box3 | null {
  root.updateMatrixWorld(true);
  const box = new THREE.Box3();
  const walk = (o: THREE.Object3D) => {
    if (!o.visible) return;
    if ((o as THREE.InstancedMesh).isInstancedMesh) {
      const inst = o as THREE.InstancedMesh;
      const arr = inst.instanceMatrix?.array;
      for (let i = 0; i < inst.count; i++) {
        _v.set(arr[i * 16 + 12], arr[i * 16 + 13], arr[i * 16 + 14]).applyMatrix4(inst.matrixWorld);
        box.expandByPoint(_v);
      }
    } else if ((o as THREE.Mesh).isMesh && (o as THREE.Mesh).geometry) {
      // Per-mesh box WITHOUT expandByObject: that helper recurses children, and
      // an InstancedMesh child would contribute its base geometry at the group
      // origin instead of its instances.
      const g = (o as THREE.Mesh).geometry;
      if (g.boundingBox === null) g.computeBoundingBox();
      if (g.boundingBox && !g.boundingBox.isEmpty()) {
        box.union(_b.copy(g.boundingBox).applyMatrix4(o.matrixWorld));
      }
    }
    for (const c of o.children) walk(c);
  };
  walk(root);
  return box.isEmpty() ? null : box;
}

/** Two successive bounds samples close enough (metres) to call the load settled. */
export function boundsStable(a: THREE.Box3, b: THREE.Box3, tolM: number): boolean {
  return a.min.distanceTo(b.min) <= tolM && a.max.distanceTo(b.max) <= tolM;
}

export interface CameraPose { position: THREE.Vector3; target: THREE.Vector3 }

const _f = new THREE.Vector3();
const _r = new THREE.Vector3();
const _u = new THREE.Vector3();
const _o = new THREE.Vector3();

/**
 * Smallest camera distance along `dir` (unit vector from target towards the
 * camera) at which every corner of `box` fits both frustum planes. Sphere
 * fits looked "centered but small" — a flat warehouse viewed diagonally
 * wastes most of a bounding sphere. Per corner the constraint is
 * |lateral| ≤ (d + depth)·tan(fov/2), solved for d in closed form.
 */
export function tightDistance(
  box: THREE.Box3, target: THREE.Vector3, dir: THREE.Vector3, aspect: number, fovDeg: number,
): number {
  const tanV = Math.tan(THREE.MathUtils.degToRad(fovDeg) / 2);
  const tanH = tanV * aspect;
  _f.copy(dir).negate(); // camera forward
  const worldUp = Math.abs(_f.y) > 0.99 ? _o.set(0, 0, -1) : _o.set(0, 1, 0);
  _r.crossVectors(_f, worldUp).normalize();
  _u.crossVectors(_r, _f);
  let d = 0;
  for (const x of [box.min.x, box.max.x])
    for (const y of [box.min.y, box.max.y])
      for (const z of [box.min.z, box.max.z]) {
        _o.set(x, y, z).sub(target);
        const depth = _o.dot(_f);
        d = Math.max(d, Math.abs(_o.dot(_r)) / tanH - depth, Math.abs(_o.dot(_u)) / tanV - depth);
      }
  return Math.max(d * 1.08, 15); // 8% breathing room; floor for tiny scenes
}

/**
 * Camera pose for a named preset, relative to the content box. The orbit
 * target sits at the box's floor center — same feel as the old fixed target,
 * just at the right place.
 */
export function fitPose(box: THREE.Box3, preset: string, aspect: number, fovDeg = 50): CameraPose {
  const size = box.getSize(new THREE.Vector3());
  const target = new THREE.Vector3((box.min.x + box.max.x) / 2, 0, (box.min.z + box.max.z) / 2);
  // Radius from the TARGET (not the box center): the target is pinned to the
  // floor, so a tall box's top corners are farther than its own half-diagonal.
  let radius = 0;
  for (const x of [box.min.x, box.max.x])
    for (const y of [box.min.y, box.max.y])
      for (const z of [box.min.z, box.max.z])
        radius = Math.max(radius, _v.set(x, y, z).distanceTo(target));

  const position = new THREE.Vector3();
  switch (preset) {
    case "top": {
      // Slightly off-vertical so the orbit azimuth stays well-defined.
      const dir = _v.set(0, 1, 0.04).normalize();
      position.copy(target).addScaledVector(dir, tightDistance(box, target, dir, aspect, fovDeg));
      break;
    }
    case "aisle": {
      // Down the long axis at forklift-driver height.
      const alongX = size.x >= size.z;
      const halfLen = Math.max(size.x, size.z) / 2;
      position.copy(target);
      if (alongX) position.x += halfLen * 1.15; else position.z += halfLen * 1.15;
      position.y = 2.4;
      target.y = 1.2;
      break;
    }
    case "dock": {
      // Low view from outside the min-corner, looking across the floor.
      const dir = _v.set(box.min.x, 0, box.min.z).sub(target).setY(0).normalize();
      position.copy(target).addScaledVector(dir, radius * 1.25);
      position.y = THREE.MathUtils.clamp(radius * 0.3, 6, 30);
      break;
    }
    default: { // iso
      const dir = _v.set(0.55, 0.68, 0.55).normalize();
      position.copy(target).addScaledVector(dir, tightDistance(box, target, dir, aspect, fovDeg));
    }
  }
  return { position, target };
}
